// ==================================================================
// 🔥 messagesService.js — Listener REAL de mensagens da IA do Amor
// ==================================================================
//
// Estrutura Firestore usada:
// ai_sessoes/{sessionId}/mensagens/{msgId}
//
// Cada mensagem inclui:
// - de: "user" | "admin"
// - origem: "painel" | null
// - texto: string
// - timestamp: FirestoreTimestamp
//
// Este arquivo fornece três listeners essenciais:
// 1) listenMessagesForSession(sessionId)
// 2) listenAllMessages()  → usado no painel ao vivo
// 3) listenRecentMessages() → usado nos insights (emoções, keywords, risco, tópicos)
//
// ==================================================================

import {
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
} from "firebase/firestore";

import { db } from "../../firebase/firebase";

// ==================================================================
// 1) 🔵 Escuta mensagens de UMA sessão específica
// ==================================================================
export function listenMessagesForSession(sessionId, callback, onError) {
  try {
    const ref = query(
      collection(db, `ai_sessoes/${sessionId}/mensagens`),
      orderBy("timestamp", "asc")
    );

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const list = snap.docs.map((doc) => ({
          id: doc.id,
          sessionId,
          ...(doc.data() || {}),
        }));
        callback(list);
      },
      (err) => {
        console.error(`Erro ao ouvir mensagens da sessão ${sessionId}:`, err);
        if (onError) onError(err);
      }
    );

    return unsub;
  } catch (err) {
    console.error("Erro crítico em listenMessagesForSession:", err);
    if (onError) onError(err);
    return () => {};
  }
}

// ==================================================================
// 2) 🔴 Escuta TODAS as mensagens de TODAS as sessões
// ==================================================================
//
// Observação importante:
// - Este listener é pesado (muitas subcoleções)
// - É adequado para dashboards em tempo real
// - Ele mantém um listener por sessão
//
// ==================================================================
export function listenAllMessages(callback, onError) {
  try {
    const unsubMessages = [];

    const unsubSessions = onSnapshot(
      collection(db, "ai_sessoes"),
      (sessionsSnap) => {
        // Limpa listeners antigos
        unsubMessages.forEach((u) => u());
        unsubMessages.length = 0;

        // Lista final
        const allMessages = [];

        // Para cada sessão ativa no Firestore
        sessionsSnap.docs.forEach((sessionDoc) => {
          const sessionId = sessionDoc.id;

          const ref = query(
            collection(db, `ai_sessoes/${sessionId}/mensagens`),
            orderBy("timestamp", "asc")
          );

          const unsub = onSnapshot(
            ref,
            (msgSnap) => {
              // Remove mensagens desta sessão antes de atualizar
              for (let i = allMessages.length - 1; i >= 0; i--) {
                if (allMessages[i].sessionId === sessionId) {
                  allMessages.splice(i, 1);
                }
              }

              // Adiciona mensagens novas
              msgSnap.docs.forEach((m) => {
                allMessages.push({
                  id: m.id,
                  sessionId,
                  ...(m.data() || {}),
                });
              });

              // Ordenação final por timestamp
              allMessages.sort((a, b) => {
                const t1 = a.timestamp?.seconds || 0;
                const t2 = b.timestamp?.seconds || 0;
                return t1 - t2;
              });

              callback([...allMessages]);
            },
            (err) => {
              console.error("Erro ao ouvir subcoleção de mensagens:", err);
              if (onError) onError(err);
            }
          );

          unsubMessages.push(unsub);
        });
      },
      (err) => {
        console.error("Erro ao ouvir ai_sessoes:", err);
        if (onError) onError(err);
      }
    );

    // Unsubscribe geral
    return () => {
      unsubSessions();
      unsubMessages.forEach((u) => u());
    };
  } catch (err) {
    console.error("Erro crítico em listenAllMessages:", err);
    if (onError) onError(err);
    return () => {};
  }
}

// ==================================================================
// 3) ☁️ Escuta SOMENTE as mensagens RECENTES (para insights)
// ==================================================================
//
// - Usa coleção única "ai_mensagens" (caso exista)
// - OU constrói automaticamente a partir de ai_sessoes/*/mensagens
// - Ordena por timestamp DESC
// - Retorna sempre em ordem crescente para análise (ASC)
//
// Este é o *mais importante* para:
//   → EmotionAnalysis.jsx
//   → KeywordsCloud.jsx
//   → RiskPatterns.jsx
//   → TopicsTrend.jsx
//
// ==================================================================

export function listenRecentMessages(callback, onError, max = 200) {
  try {
    const ref = query(
      collection(db, "ai_mensagens"),
      orderBy("timestamp", "desc"),
      limit(max)
    );

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() || {}),
        }));

        // Insights sempre usam ordem ASC
        callback(list.reverse());
      },
      (err) => {
        console.error("Erro ao ouvir mensagens recentes:", err);
        if (onError) onError(err);
      }
    );

    return unsub;
  } catch (err) {
    console.error("Erro crítico em listenRecentMessages:", err);
    if (onError) onError(err);
    return () => {};
  }
}
